import { apiService } from "./api.service";
import { IResponseModel } from "@/models/interfaces/response.model.interface";
import { IPublisher } from "@/models/interfaces/publisher/publisher.model.interface";
import { Publisher } from "@/models/publisher/publisher.model";
import { IPublisherDetailsRequest } from "@/models/interfaces/publisher/publisher-details-request.model.interface";
import { IUserPublisherAccess } from "@/models/interfaces/authentication/user-publisher-access.model.interface";
import { IProperty } from "@/models/interfaces/property/property.model.interface";
import { ResultReasonType } from "@/enums/ResultReasonType";
import { IResponseResultModel } from "@/models/interfaces/response-result.model.interface";
import { IState } from "@/models/interfaces/config/state.model.interface";

class PublisherService {

    private static serviceInstance: PublisherService;

    private constructor() { }

    public static get instance() {
        // Do you need arguments? Make it a regular method instead.
        return this.serviceInstance || (this.serviceInstance = new this());
    }

    public async create(publisher: IPublisherDetailsRequest)
        : Promise<IResponseResultModel<IPublisher>> {
        return apiService.post(`/api/v1/publisher`, publisher)
            .then((response) => {
                const result = response.data as IResponseResultModel<IPublisher>;
                return result;
            })
            .catch((error) => {
                return {
                    success: false,
                    reason: ResultReasonType.Error,
                    result: null
                };
            });
    }

    public async get()
        : Promise<IResponseModel<IPublisher[]>> {
        return apiService.get(`/api/v1/publisher`)
            .then((response) => {
                const result = response.data as IResponseModel<IPublisher[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IPublisher[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }

    public async getPublisher(publisherId: number)
        : Promise<IResponseModel<IPublisher>> {
        return apiService.get(`/api/v1/publisher/${publisherId}`)
            .then((response) => {
                const result = response.data as IResponseModel<IPublisher>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IPublisher> = {
                    success: false,
                    result: new Publisher()
                };
                return result;
            });
    }

    public async updateDetails(publisherId: number, details: IPublisherDetailsRequest)
        : Promise<boolean> {
        return apiService.patch(`/api/v1/publisher/${publisherId}`, details)
            .then((response) => {
                return true;
            })
            .catch((error) => {
                return false;
            });
    }

    public async updateState(id: number, state: IState)
    : Promise<boolean> {
      return apiService.patch(`/api/v1/publisher/${id}/state`, state)
        .then((response) => {
          return true;
        })
        .catch((error) => {
          return false;
        });
    }

    public async getUserPublishers(userId: number)
        : Promise<IResponseModel<IPublisher[]>> {
        return apiService.get(`/api/v1/user/${userId}/publisher`)
            .then((response) => {
                const result = response.data as IResponseModel<IPublisher[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IPublisher[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }

    public async addPublisherAccess(
        model: IUserPublisherAccess
    ): Promise<boolean> {
        return apiService.post(`/api/v1/user/${model.userId}/publisher/${model.publisherId}`)
            .then((response) => {
                const result = response.data.success;
                return result;
            })
            .catch((error) => {
                return false;
            });
    }

    public async deletePublisherAccess(
        model: IUserPublisherAccess
    ): Promise<boolean> {
        return apiService.delete(`/api/v1/user/${model.userId}/publisher/${model.publisherId}`)
            .then((response) => {
                const result = response.data.success;
                return result;
            })
            .catch((error) => {
                return false;
            });
    }

    public async getUserProperties(userId: number)
        : Promise<IResponseModel<IProperty[]>> {
        return apiService.get(`/api/v1/user/${userId}/publisher/property`)
            .then((response) => {
                const result = response.data as IResponseModel<IProperty[]>;
                return result;
            })
            .catch((error) => {
                const result: IResponseModel<IProperty[]> = {
                    success: false,
                    result: []
                };
                return result;
            });
    }
}

// export a singleton instance in the global namespace
export const publisherService = PublisherService.instance;
